import { Injectable, Output, EventEmitter } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { Question } from './questions'
import { Word } from './words'
import Swal from 'sweetalert2'
import { User } from './types/user'
import { Course } from './types/course'

@Injectable({
	providedIn: 'root'
})

export class ApiService {

	redirectUrl = ''

	public online = true
	public localBaseUrl = 'http://192.168.1.119/motoria-angular'
	public remoteBaseUrl = environment.remoteBaseUrl
	public baseUrl = this.online ? this.remoteBaseUrl : this.localBaseUrl
	
	public phpUrl = `${this.baseUrl}/php`
	public pdfUrl = `${this.baseUrl}/pdf`
	public phpCommonUrl = `${this.phpUrl}/common`
	public phpBrainscormUrl = `${this.phpUrl}/brainscorm`
	public phpMotoriaUrl = `${this.phpUrl}/motoria`
	
	@Output() getLoggedInName = new EventEmitter()

	constructor(private httpClient: HttpClient) { }

	public login(username: string, password: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/login.php`, { username, password })
		.pipe(map((user: User) => {
			// console.log(user)
			if (user.closed) {
				Swal.fire('Error', 'Acceso cerrado para este usuario. Por favor, contacte con el técnico.', 'error')
				return false
			} else {
				this.setUserLogin(user)
				this.getLoggedInName.emit(true)
				return user
			}
		}))
	}

	setUserLogin(user: User) {
		localStorage.setItem('user-id', user.id.toString())
		localStorage.setItem('user-name', `${user.firstname} ${user.lastname}`)
		localStorage.setItem('user-org', user.organization.id.toString())
		localStorage.setItem('user-role', user.role.id.toString())
		localStorage.setItem('user-ai', Number(user.ai).toString())
	}

	getUserID() {
		return Number(localStorage.getItem('user-id'))
	}

	getUserName() {
		return localStorage.getItem('user-name') ?? ''
	}

	getUserOrg() {
		return Number(localStorage.getItem('user-org'))
	}

	getUserRole() {
		return Number(localStorage.getItem('user-role'))
	}

	getUserAI() {
		return Boolean(Number(localStorage.getItem('user-ai')))
	}

	isLoggedIn() {
		return this.getUserID() != 0
	}

	getCourseID() {
		return Number(localStorage.getItem('course-id'))
	}

	setCourseID(id: number) {
		localStorage.setItem('course-id', id.toString())
	}

	getCourseName() {
		return localStorage.getItem('course-name') ?? ''
	}

	setCourseName(name: string) {
		localStorage.setItem('course-name', name)
	}

	getCourseFormID() {
		return Number(localStorage.getItem('course-form-id'))
	}

	setCourseFormID(id: number) {
		localStorage.setItem('course-form-id', id.toString())
	}

	getCourseFormName() {
		return localStorage.getItem('course-form-name') ?? ''
	}

	setCourseFormName(name: string) {
		localStorage.setItem('course-form-name', name)
	}

	getCourseFormHeadingWords() {
		return Number(localStorage.getItem('course-form-heading-words'))
	}

	setCourseFormHeadingWords(words: number) {
		localStorage.setItem('course-form-heading-words', words.toString())
	}

	getCourseFormCategory() {
		return Number(localStorage.getItem('course-form-category'))
	}

	setCourseFormCategory(category: number) {
		localStorage.setItem('course-form-category', category.toString())
	}

	clearCourseForm() {
		localStorage.removeItem('course-form-id')
		localStorage.removeItem('course-form-name')
		localStorage.removeItem('course-form-heading-words')
		localStorage.removeItem('course-form-category')
	}

	// Users
	public getUsers(org: number = 0, role: number = 0, name: string = '') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/users/get_users.php`, { org, role, name })
	}

	public addUser(user: User) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/users/add_user.php`, { user })
	}

	public editUser(user: User) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/users/edit_user.php`, { user })
	}

	public closeUser(id: number, closed: boolean) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/users/close_user.php`, { id, closed })
	}

	public deleteUser(id: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/users/delete_user.php`, { id })
	}

	public getUserCoursePermissions(user: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/users/get_user_course_permissions.php`, { user })
	}
	// End Users

	// Organizations
	public getOrganizations() {
		return this.httpClient.get<any>(`${this.phpUrl}/common/organizations/get_organizations.php`)
	}

	/*
	public editOrganization(org: any) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/edit_organization.php`, { org })
	}
	*/
	// End Organizations

	// Roles
	public getRoles() {
		return this.httpClient.get<any>(`${this.phpUrl}/common/roles/get_roles.php`)
	}
	// End Roles

	// Categories
	public getCategories() {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/categories/get_categories.php`, { user })
	}

	public getCategoryCourses(category: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/categories/get_category_courses.php`, { user, category })
	}

	public duplicateNode(type: string, sourceChain: string, duplicateName: string, destinationChain: string) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/tree/duplicate_nodes.php`, { user, type, sourceChain, duplicateName, destinationChain })
	}
	// End Categories

	// Courses
	public getCourse(course: number, user: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/get_course.php`, { course, user })
	}

	public getCourseInfo(course: number, user: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/course_info.php`, { course, user })
	}

	public getCourseDuration(course: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/course_duration.php`, { course })
	}
	
	public editCourse(course: Course) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/edit_course.php`, { course })
	}

	public generateBlankCourse(course: Course) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/generate_blank_course.php`, { user, course })
	}

	public courseLock(course: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/course_lock.php`, { course, user })
	}

	public courseUnlock(course: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/course_unlock.php`, { course, user })
	}

	public courseUnlockRequest(course: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/course_unlock_request.php`, { course, user })
	}

	public checkCourseErrors(course: number, multitransl: boolean, transl: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/check_course_errors.php`, { course, multitransl, transl })
	}

	public checkCourseErrorsActivitiesTests(course: number, multitransl: boolean, transl: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/check_course_errors_activities_tests.php`, { course, multitransl, transl })
	}

	public checkCourseErrorsMultimedia(course: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/check_course_errors_multimedia.php`, { course })
	}
	
	public checkCourseErrorsHtml(course: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/check_course_errors_html.php`, { course })
	}
	// End Courses

	// Units
	public getUnitInfo(unit: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/unit_info.php`, { unit })
	}

	public getCourseUnits(course: number, tree: boolean = false) {
		return this.httpClient.get<any>(`${this.phpUrl}/common/tree/course_nodes.php?course=${course}&tree=${tree}`)
	}

	public moveUnit(idunit: number, idcoursedestiny: number) {
		return this.httpClient.post<any>(`${this.phpBrainscormUrl}/moveunit/api/moveunit.php`, { idunit, idcoursedestiny })
	}
	// End Units

	// Lessons
	public getLessonInfo(lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/course/lesson_info.php`, { lesson })
	}

	public getUnitLessons(unit: number, tree: boolean = false) {
		return this.httpClient.get<any>(`${this.phpUrl}/common/tree/course_nodes.php?unit=${unit}&tree=${tree}`)
	}
	// End Lessons

	// Export Course
	public exportCourse(course: number, courseName: string = '', exportType: string = 'desktop', language: string = 'es', transl: string = 'all') {
		// return this.httpClient.post<any>(`${this.localBaseUrl}/php/brainscorm/export/course_export.php?ignorecanexport=1`, { course, courseName, exportType, language })
		return this.httpClient.post<any>(`${this.phpUrl}/brainscorm/export/course_export.php`, { course, courseName, exportType, language, transl })
	}

	public exportStatus(course: number) {
		// return this.httpClient.post<any>(`${this.localBaseUrl}/php/brainscorm/export/export_status.php`, { course })
		return this.httpClient.post<any>(`${this.phpUrl}/brainscorm/export/export_status.php`, { course })
	}

	public exportBrainscorm(userid: number, username: string, courseid: number, coursename: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/export_brainscorm.php`, { userid, username, courseid, coursename })
	}
	// End Export

	public getMoodleCourses() {
		return this.httpClient.get<any>(`${this.phpUrl}/moodle/get_moodle_courses.php`)
	}

	public exportMoodle(course: number, dir: string = '', type: string = 'desktop', language: string = 'es') {
		course = 1662
		dir = 'test'
		type = 'desktop'
		language = 'es'
		return this.httpClient.get<any>(`https://www.cursosfortec.es/formacion/bs_download.php?course=${course}&dir=${dir}&type=${type}&language=${language}`)
	}

	public showSummary(summary: number, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/heading/show_summary.php`, { summary, multitransl, transl })
	}

	public showHeading(heading: number, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/heading/show_heading.php`, { heading, multitransl, transl })
	}

	public saveCaseStudyCriteria(heading: number, criteria: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/heading/save_case_study_criteria.php`, { heading, criteria })
	}

	public getLessonMultimedia(course: number, lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/get_multimedia.php`, { course, lesson })
	}

	public deleteMultimediaItem(id: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/delete_multimedia_item.php`, { id })
	}

	public getTables(lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/tables/get_tables.php`, { lesson })
	}

	// Tree
	public insertNode(name: string, type: string, position: number, parent: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/tree/insert_node.php`, { user, name, type, position, parent })
	}

	public moveNode(id: number, type: string, oldPosition: number, oldParent: number, newPosition: number, newParent: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/tree/move_node.php`, { user, id, type, oldPosition, oldParent, newPosition, newParent })
	}

	public updateNodePositions(nodes: any) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/tree/update_node_positions.php`, { nodes })
	}

	public editNode(id: number, type: string, name: string = '', multitransl: boolean = false, transl: string = 'es') {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/tree/edit_node.php`, { user, id, type, name, multitransl, transl })
	}

	public emptyNode(id: number, type: string) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/tree/empty_node.php`, { user, id, type })
	}

	public deleteNode(id: number, type: string, position: number, parent: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/tree/delete_node.php`, { user, id, type, position, parent })
	}
	// End Tree

	public importCourse(units: any, courseID: number, courseName: string, headingWords: number, categoryID: number, service: string) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/import_course.php`, { user, units, courseID, courseName, headingWords, categoryID, service })
	}

	public deleteCourse(id: number, deleteCourseNode: boolean = false) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/delete_course.php`, { user, id, deleteCourseNode })
	}

	public wikipediaSearch(val: string, limit: number, order: string) {
		return this.httpClient.get<any>(`https://es.wikipedia.org/w/api.php?origin=*&action=query&format=json&list=search&srlimit=${limit}&srsort=${order}&srsearch=${val}`)
	}

	public generateUnitLessonsWikilinks(link: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/generate_unit_lessons_wikilinks.php`, { link })
	}

	public generateCourseWikilinks(links: Array<string>) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/generate_course_wikilinks.php`, { links })
	}

	public getUploadedFiles(userid: number, type: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/get_uploaded_files.php`, { userid, type })
	}

	public uploadFile(file: File, dir: string) {
		let form: FormData = new FormData()
		form.append('user', this.getUserID().toString())
		form.append('file', file, file.name)
		form.append('dir', dir)
		return this.httpClient.post<any>(`${this.phpUrl}/common/upload_file.php`, form)
	}

	public downloadFile(filepath: string) {
		return this.httpClient.get(`${this.phpUrl}/common/download_file.php?filepath=${filepath}`, { observe: 'response', responseType: 'blob' })
	}

	public uploadMultimedia(course: number, lesson: number, type: 'image' | 'video', file: File) {
		let form: FormData = new FormData()
		form.append('user', this.getUserID().toString())
		form.append('course', course.toString())
		form.append('lesson', lesson.toString())
		form.append('type', type)
		form.append('file', file, file.name)
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/upload_multimedia.php`, form)
	}

	public downloadMultimedia(course: number, lesson: number, type: 'all' | 'image' | 'video' = 'all') {
		return this.httpClient.post(`${this.phpUrl}/common/multimedia/download_multimedia.php`, { course, lesson, type }, { observe: 'response', responseType: 'blob' })
	}

	public editMultimedia(id: number, field: string, value: string) {
		return this.httpClient.post(`${this.phpUrl}/common/multimedia/edit_multimedia.php`, { id, field, value })
	}

	public taskStatusMoriarty(task: string) {
		return this.httpClient.get<any>(`${this.phpUrl}/motoria/espera.php?task=${task}`)
	}

	public taskStatusCelery(module: string, task: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/espera_celery.php`, { module, task })
	}

	public verifyTocPdf(userid: number, pdf: string, startPage: number, endPage: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/verify_toc_pdf.php`, { userid, pdf, startPage, endPage })
	}

	public generateCoursePdfToc(userid: number, pdf: string, outline: string, extractTables: boolean, tableBorder: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/generate_course_pdf_toc.php`, { userid, pdf, outline, extractTables, tableBorder })
	}

	public summarizeText(text: string, sentenceRatio: number, numWords: number, heading: number = 0) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/summarize_text.php`, { user, text, sentenceRatio, numWords, heading })
	}

	public paraphraseText(text: string, maxLength: number, heading: number = 0) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/paraphrase_text.php`, { user, text, maxLength, heading })
	}

	public mergeHeadings(lesson: number, selectedHeadings: Array<any>, newHeadingName: string, newHeadingContent: string) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/merge_headings.php`, { user, lesson, selectedHeadings, newHeadingName, newHeadingContent })
	}

	public splitHeadings(lesson: number, selectedHeading: any, headingContent: string, splitChars: string) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/split_headings.php`, { user, lesson, selectedHeading, headingContent, splitChars })
	}

	public suggestTitle(headingText: string, titleWords: number, numTitles: number) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/suggest_title.php`, { user, headingText, titleWords, numTitles })
	}

	public extractTextWeb(url: string, plain: boolean) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/extract_text_web.php`, { user, url, plain })
	}

	public autocompleteHeading(content?: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/autocomplete_heading.php`, { content })
	}

	public previewHeading(content?: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/heading/preview_heading.php`, { content })
	}

	public updateHeading(heading: number, name?: string, content?: string, multitransl: boolean = false, transl: string = 'es') {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/heading/update_heading.php`, { user, heading, name, content, multitransl, transl })
	}

	public updateSummary(summary: number, name?: string, content?: string, multitransl: boolean = false, transl: string = 'es') {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/heading/update_summary.php`, { user, summary, name, content, multitransl, transl })
	}

	public generateActivitiesTest(type: string, mode: string, course: number, unit: number | undefined, lesson: number | undefined, text: string | undefined, numDistractors: number, numQuestions: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/generate_activities_test.php`, { type, mode, course, unit, lesson, text, numDistractors, numQuestions })
	}

	// Activity / Test
	public getActivityTest(id: number, type: string, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/get_activity_test.php`, { id, type, multitransl, transl })
	}

	public checkActivityTestErrors(id: number, multitransl: boolean, transl: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/check_errors.php`, { id, multitransl, transl })
	}
	
	public addActivitiesTest(type: string, course: number, unit: number | undefined, lesson: number | undefined, questions: any, reset: boolean) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/add_activities_test.php`, { user, type, course, unit, lesson, questions, reset })
	}

	public addQuestion(activityTest: number, type: string, sentence: string, options: string[], solution: string, position: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/add_question.php`, { activityTest, type, sentence, options, solution, position })
	}

	public editQuestion(question: number, type: string | undefined, sentence: string | undefined, options: string[] | undefined, solution: string | undefined, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/edit_question.php`, { question, type, sentence, options, solution, multitransl, transl })
	}

	public editQuestionPositions(questions: Array<Question>) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/edit_question_positions.php`, { questions })
	}

	public deleteQuestion(question: number, position: number, activityTest: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/delete_question.php`, { question, position, activityTest })
	}

	public saveActivityTestCriteria(activityTest: number, criteria: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/save_activity_test_criteria.php`, { activityTest, criteria })
	}

	public saveActivityTestReviewHeading(activityTest: number, reviewHeading: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/activity_test/save_activity_test_review_heading.php`, { activityTest, reviewHeading })
	}
	// End Activity / Test

	// Glossary
	public wikipediaWordList(term: string) {
		return this.httpClient.get<any>(`https://es.wikipedia.org/w/api.php?origin=*&action=opensearch&search=${term}`)
	}

	public wikipediaWordDefinition(word: string) {
		return this.httpClient.get<any>(`https://es.wikipedia.org/w/api.php?origin=*&format=json&action=query&prop=extracts&exintro&explaintext&iwlinks=0&titles=${word}`)
	}

	public getGlossary(glossary: number, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/glossary/get_glossary.php`, { glossary, multitransl, transl })
	}

	public getUnitGlossary(unit: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/glossary/get_glossary.php`, { unit })
	}

	public generateGlossary(words: any, unit: number, reset: boolean) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/generate_glossary.php`, { user, words, unit, reset })
	}

	public addWord(glossary: number, word: string, definition: string, position: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/glossary/add_word.php`, { glossary, word, definition, position })
	}

	public editWord(wordId: number, word: string | undefined, definition: string | undefined, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/glossary/edit_word.php`, { wordId, word, definition, multitransl, transl })
	}

	public editWordPositions(words: Array<Word>) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/glossary/edit_word_positions.php`, { words })
	}

	public deleteWord(word: number, position: number, glossary: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/glossary/delete_word.php`, { word, position, glossary })
	}

	public importWords(glossary: number, words: any[], position: number, reset: boolean) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/glossary/import_words.php`, { glossary, words, position, reset })
	}
	// End Glossary

	// Objectives
	public getObjectives(objectives: number, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/get_objectives.php`, { objectives, multitransl, transl })
	}

	public importObjectivesText(id: number, type: string, text: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/import_objectives_text.php`, { id, type, text })
	}

	public addCapability(objectives: number, code: string | undefined, content: string | undefined) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/add_capability.php`, { objectives, code, content })
	}

	public editCapability(capability: number, code: string | undefined, content: string | undefined, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/edit_capability.php`, { capability, code, content, multitransl, transl })
	}

	public deleteCapability(id: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/delete_capability.php`, { id })
	}

	public addCriteria(capability: number, code: string | undefined, content: string | undefined) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/add_criteria.php`, { content, code, capability })
	}

	public editCriteria(criteria: number, code: string | undefined, content: string | undefined, multitransl: boolean = false, transl: string = 'es') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/edit_criteria.php`, { criteria, code, content, multitransl, transl })
	}

	public deleteCriteria(id: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/objectives/delete_criteria.php`, { id })
	}
	// End Objectives

	// Multimedia
	public getMultimedia(course: number, unit: number, lesson: number, type: string, country: string, imageType: string, imageColor: string, videoOrder: string, maxResults: number, keywords: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/get_multimedia.php`, { course, unit, lesson, type, country, imageType, imageColor, videoOrder, maxResults, keywords })
	}

	public googleImageSearch(keywords: string, maxResults: number, type: string, color: string, headingID: number = 0) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/google_image_search.php`, { keywords, maxResults, type, color, headingID })
	}

	public youtubeVideoSearch(keywords: string, maxResults: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/youtube_video_search.php`, { keywords, maxResults })
	}

	public addRecommendedMultimedia(lesson: number, heading: number, type: string, media: any[]) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/add_recommended_multimedia.php`, { user, lesson, heading, type, media })
	}

	public unitRecommendMultimedia(unit: number, type: 'images' | 'videos' | 'both', maxResults: number = 0) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/unit_recommend_multimedia.php`, { unit, type, maxResults })
	}

	public lessonRecommendMultimedia(lesson: number, type: 'images' | 'videos' | 'both', maxResults: number = 0) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/multimedia/lesson_recommend_multimedia.php`, { lesson, type, maxResults })
	}

	public extractImages(course: number, unit: number, lesson: number, file: string) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/extract_images.php`, { user, course, unit, lesson, file })
	}

	public generateImages(course: number, model: string, prompt: string, number: number, size: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/generate_images.php`, { course, model, prompt, number, size })
	}

	public saveGeneratedImages(course: number, destination: string, lesson: number, heading: number, images: any[]) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/save_generated_images.php`, { course, destination, lesson, heading, images })
	}

	public generateAudio(text: string, voice: string) {
		let user = this.getUserID()
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/generate_audio.php`, { user, text, voice })
	}

	public saveGeneratedAudio(course: number, destination: string, id: number, audio: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/motoria/save_generated_audio.php`, { course, destination, id, audio })
	}

	public uploadAudio(course: number, destination: string, id: number, audio: File) {
		let form: FormData = new FormData()
		form.append('user', this.getUserID().toString())
		form.append('course', course.toString())
		form.append('destination', destination)
		form.append('id', id.toString())
		form.append('audio', audio, audio.name)
		return this.httpClient.post<any>(`${this.phpUrl}/common/upload_audio.php`, form)
	}
	// End Multimedia

	// Files
	public getLessonFiles(lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/files/get_files.php`, { lesson })
	}

	public uploadLessonFile(lesson: number, file: File) {
		let form = new FormData()
		form.append('user', this.getUserID().toString())
		form.append('file', file, file.name)
		form.append('lesson', lesson.toString())
		return this.httpClient.post<any>(`${this.phpUrl}/common/files/upload_lesson_file.php`, form)
	}

	public editFile(id: number, desc: string = '') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/files/edit_file.php`, { id, desc })
	}

	public deleteFile(id: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/files/delete_file.php`, { id })
	}
	// End Files

	// Reports
	public generateReport(course: number, report: string, writer: string) {
		return this.httpClient.post<any>(`${this.phpBrainscormUrl}/report/api/generate_report.php`, { course, report, writer })
	}
	// End Reports

	// Tools
	public getLinks(course: number) {
		return this.httpClient.post<any>(`${this.phpBrainscormUrl}/check_links/get_links.php`, { course })
	}

	public checkLink(link: string) {
		return this.httpClient.post<any>(`${this.phpBrainscormUrl}/check_links/check_link.php`, { link })
	}

	public nodesTextSearch(course: number, nodes: any[], text: string) {
		return this.httpClient.post<any>(`${this.phpBrainscormUrl}/nodes_text_search/nodes_text_search.php`, { course, nodes, text })
	}

	public multimediaReport(course: number, type: string) {
		return this.httpClient.post<any>(`${this.phpCommonUrl}/multimedia/multimedia_report.php`, { course, type })
	}
	// End Tools

	getLogs(event: string = '', user: number = 0, start: string = '', end: string = '') {
		return this.httpClient.post<any>(`${this.phpUrl}/common/get_logs.php`, { event, user, start, end })
	}

	getEvents() {
		return this.httpClient.get<any>(`${this.phpUrl}/common/get_events.php`)
	}

	// Diagrams
	public getDiagram(parentType: string, parentID: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/diagram/get_diagram.php`, { parentType, parentID })
	}

	public updateDiagram(id: number, course: number, parentType: string, parentID: number, title: string, code: string, width: number, height: number, align: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/diagram/update_diagram.php`, { id, course, parentType, parentID, title, code, width, height, align })
	}

	public deleteDiagram(id: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/diagram/delete_diagram.php`, { id })
	}

	public downloadDiagramImage(fileName: string, imageData: any) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/diagram/download_diagram_image.php`, { fileName, imageData })
	}

	public downloadDiagramImageInk(id: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/diagram/download_diagram_image_ink.php`, { id })
	}
	// End Diagrams

	// IA
	public headingSummarizeContentAI(heading: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/heading_summarize_content.php`, { heading })
	}

	public generateHeadingContentAI(heading: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/heading_generate_content.php`, { heading })
	}

	public generateHeadingImageAI(course: number, lesson: number, heading: number, content: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/heading_generate_image.php`, { course, lesson, heading, content })
	}

	public generateDiagramAI(courseID: number, parentType: string, parentID: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/heading_generate_diagram.php`, { courseID, parentType, parentID })
	}

	public headingExtractPhrases(content: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/heading_extract_phrases.php`, { content })
	}

	public generateLessonHeadingsAI(lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/lesson_generate_headings.php`, { lesson })
	}

	public lessonSummarizeHeadingsAI(lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/lesson_summarize_headings.php`, { lesson })
	}

	public generateLessonCaseStudyAI(lesson: number, type: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/lesson_generate_case_study.php`, { lesson, type })
	}

	public generateLessonHeadingImagesAI(course: number, lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/lesson_generate_heading_images.php`, { course, lesson })
	}

	public generateActivitiesAI(lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/lesson_generate_activities.php`, { lesson })
	}

	public generateTestAI(lesson: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/lesson_generate_test.php`, { lesson })
	}

	public generateUnitSummaryAI(unit: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/unit_generate_summary.php`, { unit })
	}

	public generateUnitGlossaryAI(unit: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/unit_generate_glossary.php`, { unit })
	}

	public generateUnitLessonsStructureAI(unitId: number, unitName: string, courseName: string, lessons: number, criteria: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/unit_generate_lessons_structure.php`, { unitId, unitName, courseName, lessons, criteria })
	}

	public generateUnitLessonsContentAI(unitId: number, unitName: string, courseName: string, structure: any[], lastLesson: any, position: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/unit_generate_lessons_content.php`, { unitId, unitName, courseName, structure, lastLesson, position })
	}

	public extractPDFInfo(file: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/extract_pdf_info.php`, { file })
	}

	public translateLessonAI(lesson: number, transl: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/lesson_translate.php`, { lesson, transl })
	}

	public generateQuestionsMoodle(units: any) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/ai/generate_questions_moodle.php`, { units })
	}
	// End IA

	// Jobs
	public jobAdd(command: string, data: any) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/jobs/job_add.php`, { command, data })
	}

	public jobStatus(id: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/jobs/job_status.php`, { id })
	}

	public getCourseRunningJobs(course: number) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/jobs/get_course_running_jobs.php`, { course })
	}
	// End Jobs

	// Job Chains
	public jobChainAdd(jobs: any) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/jobs/job_chain_add.php`, { jobs })
	}

	public jobChainStatus(id: string) {
		return this.httpClient.post<any>(`${this.phpUrl}/common/jobs/job_chain_status.php`, { id })
	}
	// End Job Chains

}